import { Icon } from './Icon'
import { Select } from './Select'

export interface PaginationProps {
  /** Что листаем — для читалки: «Страницы каталога». */
  label: string
  page: number
  pageSize: number
  total: number
  sizes: readonly number[]
  onPage: (page: number) => void
  onPageSize: (size: number) => void
  /** Следующая страница ещё грузится: кнопки не жмём дважды. */
  busy?: boolean
  /**
   * `false` — сервер не говорит, сколько всего строк, и `total` — нижняя
   * граница («есть ещё хотя бы одна»). Тогда «из N» не пишем.
   */
  exact?: boolean
}

/**
 * Номера страниц вокруг текущей: первая, последняя, соседние и многоточие
 * между ними — «1 … 4 5 6 … 13». Больше семи кнопок в строке не бывает.
 */
export function pageList(page: number, pages: number): (number | 'gap')[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1)
  let from = Math.max(2, Math.min(page - 1, pages - 4))
  let to = Math.min(pages - 1, Math.max(page + 1, 5))
  // Многоточие вместо одной страницы бессмысленно — показываем её номер.
  if (from === 3) from = 2
  if (to === pages - 2) to = pages - 1
  const middle = Array.from({ length: to - from + 1 }, (_, i) => from + i)
  return [
    1,
    ...(from > 2 ? ['gap' as const] : []),
    ...middle,
    ...(to < pages - 1 ? ['gap' as const] : []),
    pages,
  ]
}

/**
 * Серверная пагинация: сколько показано из скольких, размер страницы и
 * номера. Листает сервер — экран только передаёт `page` и `page_size`.
 */
export function Pagination({
  label,
  page,
  pageSize,
  total,
  sizes,
  onPage,
  onPageSize,
  busy = false,
  exact = true,
}: PaginationProps) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, total)

  return (
    <div className="ui-pager">
      <span className="range" aria-live="polite">
        {first}–{last}
        {exact ? ` из ${total}` : null}
      </span>
      <span className="size">
        <span aria-hidden="true">На странице</span>
        <Select
          label="На странице"
          size="sm"
          value={pageSize}
          options={sizes.map((size) => ({ value: size, label: String(size) }))}
          onChange={onPageSize}
        />
      </span>
      {pages > 1 ? (
        <nav className="pages" aria-label={label}>
          <button
            type="button"
            className="ui-iconbtn"
            aria-label="Предыдущая страница"
            disabled={page <= 1 || busy}
            onClick={() => {
              onPage(page - 1)
            }}
          >
            <Icon name="i-back" />
          </button>
          {pageList(page, pages).map((item, i) =>
            item === 'gap' ? (
              <span key={`gap-${i}`} className="ellipsis" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className="num"
                aria-label={`Страница ${item}`}
                aria-current={item === page ? 'page' : undefined}
                disabled={busy}
                onClick={() => {
                  onPage(item)
                }}
              >
                {item}
              </button>
            ),
          )}
          <button
            type="button"
            className="ui-iconbtn next"
            aria-label="Следующая страница"
            disabled={page >= pages || busy}
            onClick={() => {
              onPage(page + 1)
            }}
          >
            <Icon name="i-back" />
          </button>
        </nav>
      ) : null}
    </div>
  )
}
